import { UpdateWidget } from 'modules/Editor/store/infographSlice.types';
import { InfographState, WidgetsMap } from 'types/infographTypes';
import { WidgetType } from 'types/widget.types';
import { LineWidgetData } from 'widgets/Lines/LineWidget/LineWidget.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { getCombinedRect, getWidgetRect } from 'widgets/WidgetLayout.helpers';

import { FULL_SIZE_OFFSET } from './ResponsiveLinkView.config';
import { WidgetSegmentGroup, WidgetSegmentGroupMember } from './segmentAndGroupWidgets.types';

/**
 * Sorts an array of WidgetSegmentGroup objects based on their topPx and leftPx properties.
 * The sorting is done in a top-left to bottom-right order.
 *
 * @param arr - The array of WidgetSegmentGroup objects to be sorted.
 * @returns The sorted array of WidgetSegmentGroup objects.
 */
const sortTopLeftToBottomRight = (arr: WidgetSegmentGroup[]) => {
  return arr.sort((a, b) => {
    const aFormattedLeftPx = Math.max(Math.floor(a.leftPx), 0);
    const aFormattedBottomPx = Math.floor(a.topPx + a.heightPx);

    const bFormattedLeftPx = Math.max(Math.floor(b.leftPx), 0);
    const bFormattedBottomPx = Math.floor(b.topPx + b.heightPx);

    // First, compare by topPx
    if (aFormattedBottomPx !== bFormattedBottomPx) {
      return aFormattedBottomPx - bFormattedBottomPx;
    }

    // If topPx is the same, compare by leftPx
    return aFormattedLeftPx - bFormattedLeftPx;
  });
};

const RESPONSIVE_LAYOUT = {
  // anything outside of 75% of the screen width will stacked and centered
  widthBreakpoint: 0.85,
  // gap between stacked groups - (TODO: make this based on a real distance)
  gap: 18,
  // hide lines that are rotated more than 10 degrees
  lineRotatedOffset: 10,
};

/**
 * Checks if a given widget is a rotated line.
 *
 * @param member - The widget segment group member to check.
 * @param widgets - The map of widgets.
 * @returns True if the widget segment group member represents a rotated line, false otherwise.
 */
const isRotatedLine = (member: WidgetSegmentGroupMember, widgets: WidgetsMap) => {
  const isLine = getWidgetTypeFromId(member.id) === WidgetType.Line;
  if (!isLine) return false;

  const widget = widgets[member.id] as LineWidgetData;
  const isRotated = Math.abs(widget?.endPos.yPx - widget?.startPos.yPx) >= RESPONSIVE_LAYOUT.lineRotatedOffset;

  return isRotated;
};

/**
 * Filters out rotated lines from the WidgetSegmentGroup array
 *
 * @param groupArr - An array of WidgetSegmentGroup objects.
 * @param widgets - A map of widget IDs to widget objects.
 * @returns An object containing the updated group array and an array of widgets to update.
 */
const filterOutRotatedLines = (groupArr: WidgetSegmentGroup[], widgets: WidgetsMap) => {
  const updateWidgetsArr: UpdateWidget[] = [];
  const newGroupArr: WidgetSegmentGroup[] = [];

  for (let i = 0; i < groupArr.length; i++) {
    const group = groupArr[i];

    const newMembers = group.members.filter((m) => {
      const isRotated = isRotatedLine(m, widgets);
      if (isRotated) updateWidgetsArr.push({ widgetId: m.id, widgetData: { isHidden: true } });
      return !isRotated;
    });

    if (!newMembers.length && !group.id) continue;

    const rects = newMembers.map((m) => getWidgetRect(widgets[m.id]));
    if (group.id) rects.push(getWidgetRect(widgets[group.id]));

    const newRect = getCombinedRect(rects);
    newGroupArr.push({ ...group, ...newRect, members: newMembers });
  }

  return { groupArr: newGroupArr, updateWidgetsArr };
};

/**
 * Responsively updates the position of the WidgetSegmentGroups based on the window size.
 * For smaller screens it will stack the groups and center them.
 *
 * @param originalInfograph - The original state of the infograph.
 * @param updatedInfograph - The updated state of the infograph.
 * @param segmentGroupArr - An array of widget segment groups.
 * @param windowSize - The size of the window.
 *
 * @returns An object containing the total height of the layout and an array of updated widget data.
 */
export const responsiveLayout = (
  originalInfograph: InfographState,
  updatedInfograph: InfographState,
  segmentGroupArr: WidgetSegmentGroup[],
  windowSize: { width: number; height: number },
) => {
  const originalWidgets = originalInfograph.widgets;
  const updatedWidgets = updatedInfograph.widgets;
  const pageSize = originalInfograph.size;

  const filteredGroups = filterOutRotatedLines(segmentGroupArr, originalWidgets);
  const sortedGroups = sortTopLeftToBottomRight(filteredGroups.groupArr);

  const isSmallerThanScreen = pageSize.widthPx > windowSize.width;
  if (!isSmallerThanScreen) return;

  // widgets that will be updated with topPx and leftPx
  const updateWidgetsArr: UpdateWidget[] = [...filteredGroups.updateWidgetsArr];

  const wBreak = windowSize.width * RESPONSIVE_LAYOUT.widthBreakpoint;
  const MIN_LEFT = (windowSize.width - wBreak) / 2;
  const MAX_RIGHT = MIN_LEFT + wBreak;

  // keep track of the next available top position
  let nextTopPx: number | null = null;

  for (let i = 0; i < sortedGroups.length; i++) {
    const group = sortedGroups[i];

    // calculate the new size of the group
    const rects = group.members.map((m) => getWidgetRect(updatedWidgets[m.id]));
    if (group.id) rects.push(getWidgetRect(updatedWidgets[group.id]));
    const newSize = getCombinedRect(rects);

    const groupWidth = newSize.widthPx;
    const groupHeight = newSize.heightPx;

    // group is initially full width|height - covers the entire design
    const isFullWidth = group.widthPx + FULL_SIZE_OFFSET >= pageSize.widthPx;
    const isResizedFullWidth = groupWidth + FULL_SIZE_OFFSET >= windowSize.width && newSize.leftPx === 0;

    // check if the group is outside the center screen bounds area
    const isOutsideLeft = group.leftPx < MIN_LEFT;
    const isOutsideRight = group.leftPx + groupWidth > MAX_RIGHT;

    // For centering the group
    const centeredX = (windowSize.width - groupWidth) / 2;
    const centeredXOffset = centeredX - group.leftPx;

    if ((isOutsideLeft || isOutsideRight) && !isFullWidth && !isResizedFullWidth) {
      // this is the first group that is outside the bounds
      if (nextTopPx === null) {
        // set the topPx to the next available position (for the next group)
        nextTopPx = group.topPx + groupHeight + RESPONSIVE_LAYOUT.gap;

        // center the group
        group.members.forEach((m) => {
          if (m.id !== group.id) {
            const memberNewLeftPx = originalWidgets[m.id].leftPx + centeredXOffset;
            updateWidgetsArr.push({ widgetId: m.id, widgetData: { leftPx: memberNewLeftPx } });
          }
        });

        if (group.id) {
          const memberNewLeftPx = originalWidgets[group.id].leftPx + centeredXOffset;
          updateWidgetsArr.push({ widgetId: group.id, widgetData: { leftPx: memberNewLeftPx } });
        }
        continue;
      }

      // use the next available position and shift the group down and center it
      let newTop = nextTopPx as number;
      const topOffset = newTop - group.topPx;

      group.members.forEach((m) => {
        if (m.id !== group.id) {
          const memberNewTopPx = updatedWidgets[m.id].topPx + topOffset;
          const memberNewLeftPx = originalWidgets[m.id].leftPx + centeredXOffset;
          updateWidgetsArr.push({ widgetId: m.id, widgetData: { topPx: memberNewTopPx, leftPx: memberNewLeftPx } });
        }
      });

      // if there is a group id, update it too
      if (group.id) {
        const memberNewTopPx = updatedWidgets[group.id].topPx + topOffset;
        const memberNewLeftPx = originalWidgets[group.id].leftPx + centeredXOffset;
        updateWidgetsArr.push({ widgetId: group.id, widgetData: { topPx: memberNewTopPx, leftPx: memberNewLeftPx } });
      }

      const newBottom = group.topPx + groupHeight + topOffset;
      nextTopPx = newBottom + RESPONSIVE_LAYOUT.gap;
      continue;
    }

    // the group is not outside - we just shift it down to the next available position (nextTopPx)
    if (nextTopPx !== null) {
      // shift the rest of the group down too
      let newTop = nextTopPx as number;
      const topOffset = newTop - group.topPx;

      group.members.forEach((m) => {
        const memberNewTopPx = updatedWidgets[m.id].topPx + topOffset;
        updateWidgetsArr.push({ widgetId: m.id, widgetData: { topPx: memberNewTopPx } });
      });

      if (group.id) {
        const memberNewTopPx = updatedWidgets[group.id].topPx + topOffset;
        updateWidgetsArr.push({ widgetId: group.id, widgetData: { topPx: memberNewTopPx } });
      }

      const newBottom = group.topPx + groupHeight + topOffset;
      nextTopPx = newBottom + RESPONSIVE_LAYOUT.gap;
      continue;
    }

    nextTopPx = group.topPx + groupHeight + RESPONSIVE_LAYOUT.gap;
  }

  return { totalHeight: nextTopPx === null ? 0 : Math.max(nextTopPx - RESPONSIVE_LAYOUT.gap, 0), updateWidgetsArr };
};
